import React, { useState, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/client';
import { toast } from 'react-toastify';
import CompanyLogo from '../components/CompanyLogo';

const ForceChangePassword = () => {
  const { user, loading, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not logged in — send to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Already set a permanent password — redirect to dashboard
  if (!user.mustChangePassword) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'tenant') return <Navigate to="/tenant" replace />;
    // Any custom staff-type role lands in the Staff Portal, same as 'staff'.
    return <Navigate to="/staff" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/auth/set-initial-password', { newPassword: form.newPassword });
      toast.success('Password set successfully. Welcome!');
      await fetchUser();
      // user.role is safe to read — only mustChangePassword changed
      const role = user.role;
      if (role === 'admin') navigate('/admin', { replace: true });
      else if (role === 'tenant') navigate('/tenant', { replace: true });
      // Any custom staff-type role lands in the Staff Portal, same as 'staff'.
      else navigate('/staff', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Error setting password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f7f9fc',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
              bgcolor: '#000',
              borderRadius: 1,
              py: 2,
              px: 2,
            }}
          >
            <CompanyLogo height={72} showTagline />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <LockResetIcon color="primary" />
            <Typography component="h2" variant="h6" align="center">
              Set Your Password
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            You are signed in with a temporary password. Please set a permanent password to continue.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              required
              type="password"
              label="New Password"
              autoFocus
              value={form.newPassword}
              onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
              helperText="Minimum 8 characters"
            />
            <TextField
              fullWidth
              margin="normal"
              required
              type="password"
              label="Confirm New Password"
              value={form.confirmPassword}
              onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={submitting}
            >
              {submitting ? 'Setting Password...' : 'Set Password & Continue'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Signed in as {user.email}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForceChangePassword;
